import axios from 'axios';
import { API_ENDPOINTS } from '../config/apiConfig';

// Create an axios instance with base URL and default configs
// Use learning service URL for sessions/test-related API calls
export const axiosInstance = axios.create({
  baseURL: API_ENDPOINTS.learningApi,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Whether the stored token's own `exp` has passed.
 *
 * Used to tell an expired session apart from the other reasons a request can come
 * back 401 — chiefly the entitlement gates, where practiceService and
 * sessionReviewService deliberately render an "unauthorized" state rather than
 * signing anyone out. Only a genuinely expired token should end the session.
 *
 * The claim is read, not trusted: the signature is verified server-side. Being
 * wrong here costs a redirect to a login page, not access to anything.
 */
const storedTokenHasExpired = (): boolean => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const [, payload] = token.split('.');
    if (!payload) return false;
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch {
    return false;   // unreadable token: let the caller deal with the 401
  }
};

// Add a request interceptor to include auth token in requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for common error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 on an expired token means the session is simply over, and the only
    // useful thing to do is send the student back to sign in. Previously this
    // branch was empty, so an expired session surfaced as a dashboard of
    // spinners with nothing on screen explaining why and no way forward.
    if (error.response?.status === 401 && storedTokenHasExpired()) {
      // Both copies have to go. store/index.ts mirrors auth.token into
      // localStorage on every change *and* on rehydration, so clearing only the
      // mirror is undone the moment redux-persist reloads: AuthRedirect would then
      // see a token, bounce to /select-student, hit another 401 and come back —
      // a redirect loop instead of a login page. Dropping the persisted slice as
      // well means the reload starts with no token at all.
      localStorage.removeItem('token');
      localStorage.removeItem('persist:root');
      localStorage.setItem('auth_error', 'Your session has expired. Please sign in again.');
      // assign() rather than a router navigate: a full load rebuilds the store
      // from the now-empty persisted state, which is the point.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
